import React, { useState, useRef } from 'react';
import axios from 'axios';
import config from '../../config';

interface BulkUploadModalProps {
  onSuccess: () => void;
  onClose: () => void;
}

interface UploadResult {
  created: number;
  updated: number;
  errors: { vendorId: string; error: string }[];
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({ onSuccess, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadMode, setUploadMode] = useState<'replace' | 'update' | 'append'>('update');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const baseURL = config.API_BASE_URL;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setError(null);
    setResult(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${baseURL}/vendors/template/download`, {
        headers: { 
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendor_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to download template');
      console.error('Error downloading template:', err);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('uploadMode', uploadMode);

      const response = await axios.post(
        `${baseURL}/vendors/bulk-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setResult(response.data.results);
      
      if (response.data.results.errors.length === 0) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload file');
      console.error('Error uploading file:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setError(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isValidFile = (file: File) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    return validTypes.includes(file.type) || 
           file.name.toLowerCase().endsWith('.csv') ||
           file.name.toLowerCase().endsWith('.xlsx') ||
           file.name.toLowerCase().endsWith('.xls');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content bulk-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Bulk Upload Vendors</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Instructions */}
          <div className="upload-instructions">
            <h4>Instructions</h4>
            <ol>
              <li>Download the CSV template below to see the required format</li>
              <li>Fill in your vendor and product data</li>
              <li>Choose your upload mode based on your needs</li>
              <li>Upload your completed CSV or Excel file</li>
            </ol>
          </div>

          {/* Download Template */}
          <div className="template-section">
            <h4>Template</h4>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadTemplate}
            >
              📥 Download CSV Template
            </button>
            <p className="template-note">
              The template includes example data showing the required columns:
              <code>Vendor ID</code>, <code>Vendor Name</code>, 
              <code>MATERIAL_DESC</code>, <code>MATERIAL_ID</code>, 
              <code>VENDOR_PRODUCT_NAME</code>
            </p>
          </div>

          {/* Upload Mode */}
          <div className="upload-mode-section">
            <h4>Upload Mode</h4>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="uploadMode"
                  value="update"
                  checked={uploadMode === 'update'}
                  onChange={(e) => setUploadMode(e.target.value as any)}
                />
                <span className="radio-text">
                  <strong>Update</strong> - Update existing vendors and products, add new ones
                </span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="uploadMode"
                  value="replace"
                  checked={uploadMode === 'replace'}
                  onChange={(e) => setUploadMode(e.target.value as any)}
                />
                <span className="radio-text">
                  <strong>Replace</strong> - Replace all products for existing vendors
                </span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="uploadMode"
                  value="append"
                  checked={uploadMode === 'append'}
                  onChange={(e) => setUploadMode(e.target.value as any)}
                />
                <span className="radio-text">
                  <strong>Append</strong> - Add new products without replacing existing ones
                </span>
              </label>
            </div>
          </div>

          {/* File Upload */}
          <div className="file-upload-section">
            <h4>Upload File</h4>
            <div className="file-input-container">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="file-input"
                id="bulkUploadFile"
              />
              <label htmlFor="bulkUploadFile" className="file-input-label">
                📎 Choose CSV or Excel file
              </label>
              {selectedFile && (
                <div className="selected-file">
                  <span className="file-name">{selectedFile.name}</span>
                  <button
                    type="button"
                    className="btn btn-xs btn-danger"
                    onClick={clearFile}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            <p className="file-note">
              Supported formats: CSV, Excel (.xlsx, .xls). Maximum file size: 10MB
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {/* Results Display */}
          {result && (
            <div className="upload-results">
              <h4>Upload Results</h4>
              <div className="results-summary">
                <div className="result-item success">
                  <span className="result-label">Created:</span>
                  <span className="result-value">{result.created} vendors</span>
                </div>
                <div className="result-item info">
                  <span className="result-label">Updated:</span>
                  <span className="result-value">{result.updated} vendors</span>
                </div>
                {result.errors.length > 0 && (
                  <div className="result-item error">
                    <span className="result-label">Errors:</span>
                    <span className="result-value">{result.errors.length} vendors</span>
                  </div>
                )}
              </div>

              {result.errors.length > 0 && (
                <div className="error-details">
                  <h5>Errors:</h5>
                  <ul>
                    {result.errors.map((error, index) => (
                      <li key={index}>
                        <strong>{error.vendorId}:</strong> {error.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.errors.length === 0 && (
                <div className="success-message">
                  ✅ Upload completed successfully! The modal will close automatically.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {result && result.errors.length === 0 ? 'Close' : 'Cancel'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!selectedFile || loading || !isValidFile(selectedFile!)}
          >
            {loading ? 'Uploading...' : 'Upload File'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
